import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import Decimal from 'decimal.js';
import * as moment from 'moment';
import { Moment } from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { BookingQuotation } from '../booking-quotation';
import { LviBankInformation } from '../lvi-bank-information';
import { SalesProduct } from '../sales-product';
import { SalesProductType } from '../sales-product-type';
import { DecimalValidator } from '../validator/decimal-validator';
import { NumberValidator } from '../validator/number-validator';
import { BookingService } from './booking.service';
import { CurrencyService } from './currency.service';
import { ExchangeRateService } from './exchange-rate.service';
import { ContactRequestQuotation } from '../contact-request-quotation';

@Injectable({
  providedIn: 'root'
})
export class BookingPreConfirmationService {

  preConfirmationForm!: FormGroup;
  productTypes: SalesProductType[] = [];
  displayPreConfirmationForm = false;
  bankInformations: LviBankInformation[] = [];

  readonly TRAVEL_INSURANCE_ID = 15;
  readonly GARANTIE_ANNULATION_ID = 7;
  readonly GARANTIE_ANNULATION_RATE = new Decimal(0.04);

  public totalPriceInBaseCurrency: Decimal;
  public totalPriceInSecondCurrency: Decimal;
  public totalReceivedInBaseCurrency: Decimal;
  public totalReceivedInSecondCurrency: Decimal;
  public totalGarantieAnnulationInBaseCurrency: Decimal;
  public totalGarantieAnnulationInSecondCurrency: Decimal;

  productPriceInCurrency: Decimal[] = [];
  productPriceInSecondCurrency: Decimal[] = [];

  creatingQuotation = false;
  cloningQuotation = false;
  showIngQuotationList = true;
  quotationToClone!: BookingQuotation;

  constructor(
    private builder: FormBuilder,
    private currencyService: CurrencyService,
    private exchangeRateService: ExchangeRateService,
    private bookingService: BookingService
  ) {
    this.totalPriceInBaseCurrency = new Decimal(0.00);
    this.totalPriceInSecondCurrency = new Decimal(0.00);
    this.totalReceivedInBaseCurrency = new Decimal(0.00);
    this.totalReceivedInSecondCurrency = new Decimal(0.00);
    this.totalGarantieAnnulationInBaseCurrency = new Decimal(0.00);
    this.totalGarantieAnnulationInSecondCurrency = new Decimal(0.00);
   }

   addProduct() {
    let array = this.preConfirmationForm.get("products") as FormArray;
    array.controls.push(this.builder.group({
      type: this.productTypes[0],
      description: "",
      amount: [new Decimal(0.00), Validators.compose([DecimalValidator, NumberValidator])],
      currency: this.currencyService.currencies[0],
      activeMenu: false
    }));
  }

  insertItemToLast(confirmationDate: Moment) {
    let itemArray = this.preConfirmationForm.get("products") as FormArray;
    this.insertAt(itemArray.controls, itemArray.controls.length, this.builder.group({
      type: this.productTypes[0],
      description: "",
      amount: [new Decimal(0.00), Validators.compose([DecimalValidator, NumberValidator])],
      currency: this.currencyService.currencies[0],
      activeMenu: false
    }));
    this.updateTotalPriceInCurrency(confirmationDate);
  }

  
  initProducts(productList: SalesProduct[], confirmationDate: Moment) {
    let array = this.preConfirmationForm.get("products") as FormArray;
    for (let product of productList) {
      array.controls.push(this.builder.group({
        type: product.productType,
        description: product.description,
        amount: [product.amount.toFixed(2), Validators.compose([DecimalValidator, NumberValidator])],
        currency: product.currency,
        activeMenu: false
      }));
    }
    if (array.controls.length == 0) {
      this.insertItem(1);
    }
    this.updateTotalPriceInCurrency(confirmationDate);
  }

  insertItem(i: number) {
    let itemArray = this.preConfirmationForm.get("products") as FormArray;
    this.insertAt(itemArray.controls, i + 1, this.builder.group({
      type: this.productTypes[0],
      description: "",
      amount: [new Decimal(0.00), Validators.compose([DecimalValidator, NumberValidator])],
      currency: this.currencyService.currencies[0],
      activeMenu: false
    }));
    this.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  insertAt(array: AbstractControl[], index: number, element: FormGroup) {
    array.splice(index, 0, element);
  }

  private get preConfirmationDate(): Moment{
    if(this.preConfirmationForm.get("confirmationDate")!.value){
      return moment(this.preConfirmationForm.get("confirmationDate")!.value).utc(true);
    } else {
      return moment().utc(true);
    }
  }

  removeItem(i: number) {
    let itemArray = this.preConfirmationForm.get("products") as FormArray;
    itemArray.controls.splice(i, 1);
    this.productPriceInCurrency.splice(i, 1);
    if (this.productPriceInSecondCurrency[i]) {
      this.productPriceInSecondCurrency.splice(i, 1);
    }
    if (itemArray.controls.length == 0) {
      this.addProduct();
    }
    this.updateTotalPriceInCurrency(this.preConfirmationDate);
  }

  moveUp(i: number) {
    let itemArray = this.preConfirmationForm.get("products") as FormArray;
    this.moveItem(itemArray.controls, i, -1);
  }

  moveDown(i: number) {
    let itemArray = this.preConfirmationForm.get("products") as FormArray;
    this.moveItem(itemArray.controls, i, 1);
  }

  moveItem(array: any[], i: number, delta: number) {
    let newIndex = i + delta;
    if (newIndex < 0 || newIndex >= array.length) {
      return;
    }
    let indexes = [i, newIndex].sort();
    array.splice(indexes[0], 2, array[indexes[1]], array[indexes[0]]);
  }

  updateTotalPriceInCurrency(confirmationDate: moment.Moment) {
    let productFormArray = this.preConfirmationForm.get("products") as FormArray;
    let totalInBase = new Decimal(0.00);
    let totalCancellationAndInsuranceInBase = new Decimal(0.00);
    let totalCancellationAndInsuranceInSecond = new Decimal(0.00);
    let priceInBaseObs: Observable<Decimal>[] = [];
    let cancellationOrInsuranceObs = [];
    let cancellationOrInsuranceInSecondObs = [];

    for (let productForm of productFormArray.controls) {
      const index = productFormArray.controls.indexOf(productForm);
      let newPrice = this.exchangeRateService.calculatePriceInCurrency(new Decimal(productForm.value.amount), productForm.value.currency, 
      this.preConfirmationForm.value.baseCurrency, this.preConfirmationForm.value.baseCurrency, confirmationDate);
      priceInBaseObs.push(newPrice);
      newPrice.subscribe(newValue => {
        this.productPriceInCurrency[index] = newValue.ceil();
      });
      if (productForm.value.type && (productForm.value.type.id == this.TRAVEL_INSURANCE_ID || productForm.value.type.id == this.GARANTIE_ANNULATION_ID)) {
        cancellationOrInsuranceObs.push(newPrice);
      }
    }
    if (cancellationOrInsuranceObs.length == 0) {
      cancellationOrInsuranceObs.push(of(new Decimal(0)));
    }
    let totalInBaseObs = forkJoin(priceInBaseObs);
    let cancellationObs = forkJoin(cancellationOrInsuranceObs);
    forkJoin([totalInBaseObs, cancellationObs]).subscribe(([totalPrices, cancellationAndInsurancePrices]) => {
      for (let price of totalPrices) {
        totalInBase = totalInBase.plus(price.ceil());
      }
      this.totalPriceInBaseCurrency = totalInBase;
      for (let price of cancellationAndInsurancePrices) {
        totalCancellationAndInsuranceInBase = totalCancellationAndInsuranceInBase.plus(price);
      }
      let garantieInBaseNet = totalInBase.minus(totalCancellationAndInsuranceInBase).times(this.GARANTIE_ANNULATION_RATE);
      this.totalGarantieAnnulationInBaseCurrency = garantieInBaseNet;
    });

    let totalInSecond = new Decimal(0.00);
    this.totalPriceInSecondCurrency = totalInSecond;
    if (this.preConfirmationForm.value.secondCurrency) {
      let priceInSecondObs: Observable<Decimal>[] = [];
      for (let productForm of productFormArray.controls) {
        const index = productFormArray.controls.indexOf(productForm);
        let newPrice = this.exchangeRateService.calculatePriceInCurrency(new Decimal(productForm.value.amount), productForm.value.currency, 
        this.preConfirmationForm.value.secondCurrency, this.preConfirmationForm.value.baseCurrency, confirmationDate);
        priceInSecondObs.push(newPrice);
        newPrice.subscribe(newValue => {
          this.productPriceInSecondCurrency[index] = newValue.ceil();
        });
        if (productForm.value.type.id == this.TRAVEL_INSURANCE_ID || productForm.value.type.id == this.GARANTIE_ANNULATION_ID) {
          cancellationOrInsuranceInSecondObs.push(newPrice);
        }
      }
      if (cancellationOrInsuranceInSecondObs.length == 0) {
        cancellationOrInsuranceInSecondObs.push(of(new Decimal(0)));
      }

      let totalInSecondObs = forkJoin(priceInSecondObs);
      let totalCancellationOrInsuranceInSecondObs = forkJoin(cancellationOrInsuranceInSecondObs);
      forkJoin([totalInSecondObs, totalCancellationOrInsuranceInSecondObs]).subscribe(([totalPrices, cancellationAndInsurancePrices]) => {
        for (let price of totalPrices) {
          totalInSecond = totalInSecond.plus(price.ceil());
        }
        this.totalPriceInSecondCurrency = totalInSecond;
        for (let price of cancellationAndInsurancePrices) {
          totalCancellationAndInsuranceInSecond.plus(price);
        }
        let garantieInSecondNet = totalInSecond.minus(totalCancellationAndInsuranceInSecond).times(this.GARANTIE_ANNULATION_RATE);
        this.totalGarantieAnnulationInSecondCurrency = garantieInSecondNet;
      });
    }
  }

  initPreConfirmationForm(){
    let preconf = this.bookingService.booking.preConfirmation!;
    this.preConfirmationForm = this.builder.group({
      description: preconf.description,
      comment: preconf.comment,
      products: this.builder.array([]),
      baseCurrency: preconf.baseCurrency,
      secondCurrency: preconf.secondCurrency,
      confirmationDate: preconf.confirmationDate==null?null:moment(preconf.confirmationDate).toDate(),
      dueDate: preconf.dueDate==null?null:moment(preconf.dueDate).toDate(),
      deposit: preconf.deposit,
      priceIncludes: preconf.priceIncludes,
      priceNotIncludes: preconf.priceNotIncludes,
      selectedBank: preconf.selectedBank,
      middleComment: preconf.middleComment
    });
  }

  calculateProductPricesInCurrency(confirmationDate: Moment) {
    this.productPriceInCurrency = [];
    this.productPriceInSecondCurrency = [];
    let productFormArray = this.preConfirmationForm.get("products") as FormArray;
    for (let product of productFormArray.controls) {
      const index = productFormArray.controls.indexOf(product);
      this.exchangeRateService.calculatePriceInCurrency(new Decimal(product.get("amount")!.value), product.get("currency")!.value,
        this.preConfirmationForm.get("baseCurrency")!.value, this.preConfirmationForm.get("baseCurrency")!.value, confirmationDate).subscribe(amount => {
          this.productPriceInCurrency[index] = amount;
        });
      if (this.preConfirmationForm.get("secondCurrency")!.value) {
        this.exchangeRateService.calculatePriceInCurrency(new Decimal(product.get("amount")!.value), product.get("currency")!.value,
          this.preConfirmationForm.get("secondCurrency")!.value, this.preConfirmationForm.get("secondCurrency")!.value, confirmationDate).subscribe(amount => {
            this.productPriceInSecondCurrency[index] = amount;
          });
      }
    }
  }

}
